import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getCallById } from '../services/apiService';
import TranscriptView from '../components/TranscriptView';
import { CallDetailResponse } from '../types';

/**
 * CallDetailPage Component
 * Displays detailed information about a single call including audio and transcript
 */
const CallDetailPage: React.FC = () => {
  const { callId } = useParams<{ callId: string }>();
  const navigate = useNavigate();

  const [callDetails, setCallDetails] = useState<CallDetailResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);



  useEffect(() => {
    const fetchCallDetails = async () => {
      try {
        setIsLoading(true);
        setError(null);

        if (!callId) {
          throw new Error('Call ID is required');
        }
        const data = await getCallById({ callId });
        setCallDetails(data);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to fetch call details';
        setError(errorMessage);
      } finally {
        setIsLoading(false);
      }
    };

    if (callId) {
      fetchCallDetails();
    }
  }, [callId]);

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const formatDuration = (seconds: number | null | undefined) => {
    if (seconds === null || seconds === undefined) return 'N/A';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}m ${remainingSeconds}s`;
  };

  const formatCost = (cost: number | null | undefined) => {
    if (cost === null || cost === undefined) return 'N/A';
    return `$${cost.toFixed(4)}`;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        <span className="ml-3 text-lg theme-text-secondary font-sans">Loading call details...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-96">
        <div className="theme-status-error rounded-lg p-6 max-w-md">
          <h2 className="text-lg font-semibold theme-text-primary mb-2 font-sans">Error Loading Call</h2>
          <p className="theme-text-secondary mb-4 font-sans">{error}</p>
          <button
            onClick={() => navigate('/calls')}
            className="theme-button-primary font-sans"
          >
            Back to Call Logs
          </button>
        </div>
      </div>
    );
  }

  if (!callDetails) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <h2 className="text-xl font-semibold theme-text-primary mb-2 font-sans">Call Not Found</h2>
          <p className="theme-text-secondary mb-4 font-sans">The requested call could not be found.</p>
          <button
            onClick={() => navigate('/calls')}
            className="theme-button-primary font-sans"
          >
            Back to Call Logs
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="call-detail-container">
      <div className="call-detail-content space-y-6 font-sans">
        {/* Header with back button */}
        <div className="flex flex-col space-y-4">
          <button
            onClick={() => navigate('/calls')}
            className="flex items-center theme-accent-primary hover:theme-accent-secondary transition-colors self-start font-sans"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Call Logs
          </button>
          <h1 className="text-3xl font-bold theme-text-primary font-sans">Call Details</h1>
        </div>

        {/* Call Information Card */}
        <div className="theme-card content-spacing-default">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium theme-text-secondary font-sans">Start Time</label>
              <p className="text-lg theme-text-primary font-sans">{callDetails.start_time ? formatDateTime(callDetails.start_time) : 'N/A'}</p>
            </div>
            <div>
              <label className="block text-sm font-medium theme-text-secondary font-sans">Duration</label>
              <p className="text-lg theme-text-primary font-sans">{formatDuration(callDetails.duration_seconds)}</p>
            </div>
            <div>
              <label className="block text-sm font-medium theme-text-secondary font-sans">Cost</label>
              <p className="text-lg theme-text-primary font-sans">{formatCost(callDetails.total_cost)}</p>
            </div>
            <div>
              <label className="block text-sm font-medium theme-text-secondary font-sans">Status</label>
              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full font-sans ${callDetails.status === 'ended'
                ? 'theme-status-success'
                : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400'
                }`}>
                {callDetails.status}
              </span>
            </div>
          </div>

          {/* Summary */}
          {callDetails.summary && (
            <div className="mb-6">
              <label className="block text-sm font-medium theme-text-secondary mb-2 font-sans">Call Summary</label>
              <div className="theme-bg-secondary rounded-lg p-4">
                <p className="theme-text-primary font-sans">{callDetails.summary}</p>
              </div>
            </div>
          )}

          {/* Audio Player */}
          {callDetails.recording_url && (
            <div className="mb-6">
              <label className="block text-sm font-medium theme-text-secondary mb-2 font-sans">Call Recording</label>
              <audio 
                controls 
                className="w-full"
                preload="metadata"
              >
                <source src={callDetails.recording_url} type="audio/wav" />
                <source src={callDetails.recording_url} type="audio/mpeg" />
                Your browser does not support the audio element.
              </audio>
            </div>
          )}
        </div>

        {/* Transcript */}
        <div className="theme-card content-spacing-default">
          <TranscriptView messages={callDetails.messages} />
        </div>
      </div>
    </div>
  );
};

export default CallDetailPage;